/**
 * Where request-mode hours come from, for a professional whose calendar cannot
 * be checked.
 *
 * A preferred-time request has no calendar to read, so the times offered are
 * the professional's configured working hours rather than real availability:
 * the booking link the visitor was sent to, else the professional's own
 * bookable link, else a plain working week. Everything here is pure so the
 * resolution can be tested without a server.
 */

/** Working day offered when nothing is configured. */
export const REQUEST_START_HOUR = 9;
export const REQUEST_END_HOUR = 17;

/** Gap between offered start times, in minutes. */
export const REQUEST_SLOT_INTERVAL_MINUTES = 30;

/** Meeting lengths offered when nothing is configured, in minutes. The first is the default. */
export const REQUEST_DURATIONS = [30, 60];

/** Days generated ahead of today, matching the connected card's two-week window. */
export const REQUEST_WINDOW_DAYS = 14;

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;
const HOURS_PER_HALF_DAY = 12;

/** Monday to Friday, as dayjs weekday numbers (0 = Sunday). */
const WORKING_WEEKDAYS = [1, 2, 3, 4, 5];

const WEEKDAY_BY_NAME: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
};

/** Which configuration the offered hours were read from. */
export type RequestConfigSource =
    | 'session'
    | 'clientDirectorSession'
    | 'default';

/** One bookable stretch of a day, in minutes from local midnight. */
export interface RequestTimeWindow {
    startMinutes: number;
    endMinutes: number;
}

/** A closed stretch of dates, inclusive at both ends, as YYYY-MM-DD. */
export interface RequestUnavailableRange {
    startDate: string;
    endDate: string;
}

export interface RequestAvailabilityConfig {
    source: RequestConfigSource;
    /** The booking link the hours were read from; null for the default week. */
    sessionId: number | null;
    sessionName: string | null;
    /** IANA zone the hours are written in. */
    timezone: string;
    /** Windows per weekday (0 = Sunday). A weekday with no entry is closed. */
    weekdayWindows: Partial<Record<number, RequestTimeWindow[]>>;
    durations: number[];
    unavailableRanges: RequestUnavailableRange[];
    intervalMinutes: number;
    windowDays: number;
    locationType: string;
    locationString: string | null;
    customMessage: string | null;
    meetingCover: string | null;
    /** The link's own meeting type: `one-on-one`, or `random` for round robin. */
    meetingType: string | null;
}

/**
 * The subset of a CalendarSession that decides request-mode hours. Both the
 * `getCalendarSession` payload and a professional's
 * `ClientDirectorCalendarSession` selection satisfy it.
 */
export interface RequestSessionConfigSource {
    ID: number;
    SessionName?: string | null;
    Timezone?: string | null;
    MeetingType?: string | null;
    MeetingCover?: string | null;
    CustomMessage?: string | null;
    LocationType?: string | null;
    LocationString?: string | null;
    IsActive?: boolean | null;
    Durations?: Array<{
        Duration?: number | null;
        Checked?: boolean | null;
    } | null> | null;
    Availability?: Array<{
        Day?: string | null;
        IsChecked?: boolean | null;
        TimeSlots?: Array<{
            StartTime?: string | null;
            EndTime?: string | null;
        } | null> | null;
    } | null> | null;
    UnavailableDateRanges?: Array<{
        StartDate?: string | null;
        EndDate?: string | null;
    } | null> | null;
    Professional?: { ID?: number | null } | null;
}

export interface ResolveRequestConfigInput {
    /** The professional whose hours are being offered. */
    professionalId: number | null | undefined;
    /** Their own timezone, used when a link names none the runtime knows. */
    fallbackTimezone: string;
    /** The booking link the visitor arrived through, when one was named. */
    requestedSession?: RequestSessionConfigSource | null;
    /** The professional's own bookable link, when they have one. */
    clientDirectorSession?: RequestSessionConfigSource | null;
}

/** Whether a string names a timezone the runtime can resolve. */
export const isKnownTimezone = (zone: string | null | undefined): boolean => {
    if (!zone) return false;
    try {
        new Intl.DateTimeFormat(undefined, { timeZone: zone });
        return true;
    } catch {
        return false;
    }
};

/**
 * Minutes from midnight for a slot time as Business Manager stores it: either
 * `9:00 AM` / `12:30 pm` or 24-hour `09:00` / `17:30`. Null when unreadable,
 * so a malformed slot is dropped instead of opening the day at midnight.
 */
export const parseTimeToMinutes = (
    value: string | null | undefined,
): number | null => {
    const match = /(\d{1,2}):(\d{2})\s*(am|pm)?/i.exec(value?.trim() ?? '');
    if (!match) return null;

    let hour = Number(match[1]);
    const minute = Number(match[2]);
    const meridiem = match[3]?.toLowerCase();
    // A 12-hour time runs 1-12; a 24-hour one 0-24 (24:00 closes the day).
    const hourInRange = meridiem
        ? hour >= 1 && hour <= HOURS_PER_HALF_DAY
        : hour <= 2 * HOURS_PER_HALF_DAY;
    if (!hourInRange) return null;

    if (meridiem === 'pm' && hour !== HOURS_PER_HALF_DAY) {
        hour += HOURS_PER_HALF_DAY;
    } else if (meridiem === 'am' && hour === HOURS_PER_HALF_DAY) {
        hour = 0;
    }

    const minutes = hour * MINUTES_PER_HOUR + minute;
    return minute < MINUTES_PER_HOUR && minutes <= MINUTES_PER_DAY
        ? minutes
        : null;
};

const defaultWeekdayWindows = (): RequestAvailabilityConfig['weekdayWindows'] =>
    Object.fromEntries(
        WORKING_WEEKDAYS.map((weekday) => [
            weekday,
            [
                {
                    startMinutes: REQUEST_START_HOUR * MINUTES_PER_HOUR,
                    endMinutes: REQUEST_END_HOUR * MINUTES_PER_HOUR,
                },
            ],
        ]),
    );

/** The plain working week offered when no booking link is configured. */
export const defaultRequestConfig = (
    timezone: string,
): RequestAvailabilityConfig => ({
    source: 'default',
    sessionId: null,
    sessionName: null,
    timezone,
    weekdayWindows: defaultWeekdayWindows(),
    durations: [...REQUEST_DURATIONS],
    unavailableRanges: [],
    intervalMinutes: REQUEST_SLOT_INTERVAL_MINUTES,
    windowDays: REQUEST_WINDOW_DAYS,
    locationType: 'online',
    locationString: null,
    customMessage: null,
    meetingCover: null,
    meetingType: null,
});

const windowsFromSlots = (
    slots: NonNullable<
        NonNullable<RequestSessionConfigSource['Availability']>[number]
    >['TimeSlots'],
): RequestTimeWindow[] =>
    (slots ?? []).flatMap((slot) => {
        const startMinutes = parseTimeToMinutes(slot?.StartTime);
        const endMinutes = parseTimeToMinutes(slot?.EndTime);
        if (startMinutes === null || endMinutes === null) return [];
        return endMinutes > startMinutes ? [{ startMinutes, endMinutes }] : [];
    });

/**
 * Open windows per weekday from the link's Availability. A day counts only when
 * it is ticked and has at least one readable slot, the same days the calendar
 * grid would offer for that link.
 */
export const weekdayWindowsFromSession = (
    session: RequestSessionConfigSource,
): RequestAvailabilityConfig['weekdayWindows'] => {
    const windows: RequestAvailabilityConfig['weekdayWindows'] = {};
    for (const entry of session.Availability ?? []) {
        const weekday = WEEKDAY_BY_NAME[entry?.Day?.trim().toLowerCase() ?? ''];
        if (!entry?.IsChecked || weekday === undefined) continue;
        const dayWindows = windowsFromSlots(entry.TimeSlots);
        if (dayWindows.length > 0) {
            windows[weekday] = [...(windows[weekday] ?? []), ...dayWindows];
        }
    }
    return windows;
};

/**
 * Ticked durations only, matching the calendar grid, which treats an unticked
 * or unset box as not offered.
 */
export const durationsFromSession = (
    session: RequestSessionConfigSource,
): number[] => {
    const durations = (session.Durations ?? [])
        .filter((entry) => entry?.Checked === true)
        .map((entry) => Number(entry?.Duration))
        .filter((duration) => Number.isInteger(duration) && duration > 0);
    return [...new Set(durations)].sort((a, b) => a - b);
};

const dateKey = (value: string | null | undefined): string | null => {
    const key = value?.trim().slice(0, 'YYYY-MM-DD'.length) ?? '';
    return /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : null;
};

export const unavailableRangesFromSession = (
    session: RequestSessionConfigSource,
): RequestUnavailableRange[] =>
    (session.UnavailableDateRanges ?? []).flatMap((range) => {
        const startDate = dateKey(range?.StartDate);
        const endDate = dateKey(range?.EndDate);
        return startDate && endDate && startDate <= endDate
            ? [{ startDate, endDate }]
            : [];
    });

const hasOpenHours = (
    windows: RequestAvailabilityConfig['weekdayWindows'],
): boolean => Object.values(windows).some((day) => (day?.length ?? 0) > 0);

/**
 * A link's configuration as request-mode hours, or null when the link cannot
 * speak for this professional: switched off, owned by someone else, or without
 * a single open hour. The caller falls through to the next source.
 *
 * A link with open hours but no ticked duration keeps its hours and takes the
 * default lengths, so a half-configured link still shapes the week.
 */
export const requestConfigFromSession = (
    session: RequestSessionConfigSource | null | undefined,
    source: Exclude<RequestConfigSource, 'default'>,
    professionalId: number | null | undefined,
    fallbackTimezone: string,
): RequestAvailabilityConfig | null => {
    if (!session?.ID || session.IsActive === false) return null;
    if (!professionalId || session.Professional?.ID !== professionalId) {
        return null;
    }

    const weekdayWindows = weekdayWindowsFromSession(session);
    if (!hasOpenHours(weekdayWindows)) return null;

    const durations = durationsFromSession(session);
    const defaults = defaultRequestConfig(fallbackTimezone);

    return {
        ...defaults,
        source,
        sessionId: session.ID,
        sessionName: session.SessionName?.trim() || null,
        timezone: isKnownTimezone(session.Timezone)
            ? String(session.Timezone)
            : fallbackTimezone,
        weekdayWindows,
        durations: durations.length > 0 ? durations : defaults.durations,
        unavailableRanges: unavailableRangesFromSession(session),
        locationType: session.LocationType?.trim() || defaults.locationType,
        locationString: session.LocationString?.trim() || null,
        customMessage: session.CustomMessage?.trim() || null,
        meetingCover: session.MeetingCover?.trim() || null,
        meetingType: session.MeetingType ?? null,
    };
};

/**
 * The hours to offer, in priority order: the link the visitor was sent
 * through, then the professional's own bookable link, then the default week.
 */
export const resolveRequestAvailabilityConfig = ({
    professionalId,
    fallbackTimezone,
    requestedSession,
    clientDirectorSession,
}: ResolveRequestConfigInput): RequestAvailabilityConfig =>
    requestConfigFromSession(
        requestedSession,
        'session',
        professionalId,
        fallbackTimezone,
    ) ??
    requestConfigFromSession(
        clientDirectorSession,
        'clientDirectorSession',
        professionalId,
        fallbackTimezone,
    ) ??
    defaultRequestConfig(fallbackTimezone);

/** Whether a YYYY-MM-DD date falls inside any closed range. */
export const isDateUnavailable = (
    date: string,
    ranges: RequestUnavailableRange[],
): boolean =>
    ranges.some((range) => date >= range.startDate && date <= range.endDate);
